import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, Restaurant, RestaurantMetrics, DashboardKPI } from '@/lib/supabase';

// ============================================================================
// Hook: useRestaurants - Récupère tous les restaurants
// ============================================================================
export const useRestaurants = () => {
  return useQuery({
    queryKey: ['restaurants'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      return data as Restaurant[];
    },
  });
};

// ============================================================================
// Hook: useRestaurant - Récupère un restaurant par ID ou slug
// ============================================================================
export const useRestaurant = (idOrSlug: string) => {
  return useQuery({
    queryKey: ['restaurant', idOrSlug],
    queryFn: async () => {
      // Essayer d'abord par slug, puis par id
      let query = supabase
        .from('restaurants')
        .select('*')
        .eq('slug', idOrSlug)
        .single();

      let { data, error } = await query;

      // Si pas trouvé par slug, essayer par id
      if (error && error.code === 'PGRST116') {
        const queryById = supabase
          .from('restaurants')
          .select('*')
          .eq('id', idOrSlug)
          .single();

        const result = await queryById;
        data = result.data;
        error = result.error;
      }

      if (error) throw error;
      return data as Restaurant;
    },
    enabled: !!idOrSlug,
  });
};

// ============================================================================
// Hook: useRestaurantMetrics - Récupère les métriques d'un restaurant
// ============================================================================
export const useRestaurantMetrics = (restaurantId: string) => {
  return useQuery({
    queryKey: ['restaurant-metrics', restaurantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('restaurant_metrics')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('period_start', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data as RestaurantMetrics | null;
    },
    enabled: !!restaurantId,
  });
};

// ============================================================================
// Hook: useDashboardKPIs - Récupère les KPIs via la vue
// ============================================================================
export const useDashboardKPIs = (restaurantId?: string) => {
  return useQuery({
    queryKey: ['dashboard-kpis', restaurantId],
    queryFn: async () => {
      let query = supabase.from('dashboard_kpis').select('*');

      if (restaurantId) {
        query = query.eq('id', restaurantId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as DashboardKPI[];
    },
  });
};

// ============================================================================
// Hook: useCreateRestaurant - Crée un nouveau restaurant
// ============================================================================
export const useCreateRestaurant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (restaurant: Partial<Restaurant>) => {
      const { data, error } = await supabase
        .from('restaurants')
        .insert(restaurant)
        .select()
        .single();

      if (error) throw error;
      return data as Restaurant;
    },
    onSuccess: () => {
      // Invalider le cache pour recharger la liste
      queryClient.invalidateQueries({ queryKey: ['restaurants'] });
    },
  });
};

// ============================================================================
// Hook: useUpdateRestaurant - Met à jour un restaurant
// ============================================================================
export const useUpdateRestaurant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Restaurant>;
    }) => {
      const { data, error } = await supabase
        .from('restaurants')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Restaurant;
    },
    onSuccess: (data) => {
      // Invalider le cache
      queryClient.invalidateQueries({ queryKey: ['restaurants'] });
      queryClient.invalidateQueries({ queryKey: ['restaurant', data.id] });
      queryClient.invalidateQueries({ queryKey: ['restaurant', data.slug] });
    },
  });
};

// ============================================================================
// Hook: useDeleteRestaurant - Supprime un restaurant
// ============================================================================
export const useDeleteRestaurant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('restaurants').delete().eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restaurants'] });
    },
  });
};

